(function (root) {
  'use strict';
  if (root.LibasPdpDeliveryEstimates) return;

  var cache = new Map();
  var generation = 0;
  var destinationKey = '';
  var pending = new Map();
  var timer = null;
  var active = null;
  var TTL = 30000;
  var ENDPOINT = '/apps/libas-delivery-timelines/pdp-live-estimates.php?theme_id=147927531670';

  function failure(code) {
    var error = new Error('Product delivery estimate unavailable.');
    error.code = code;
    return error;
  }

  function destination(pin, coordinates) {
    var value = {drop_pincode: String(pin || '')};
    if (!/^[1-9][0-9]{5}$/.test(value.drop_pincode)) throw failure('INVALID_PIN');
    if (coordinates) {
      if (coordinates.lat == null || coordinates.lng == null || String(coordinates.lat).trim() === '' || String(coordinates.lng).trim() === '') {
        throw failure('INVALID_COORDINATES');
      }
      var lat = Number(coordinates.lat);
      var lng = Number(coordinates.lng);
      if (!Number.isFinite(lat) || !Number.isFinite(lng) || Math.abs(lat) > 90 || Math.abs(lng) > 180) {
        throw failure('INVALID_COORDINATES');
      }
      value.drop_latitude = formatApiCoordinate(coordinates.lat);
      value.drop_longitude = formatApiCoordinate(coordinates.lng);
      if (Math.abs(Number(value.drop_latitude)) > 90 || Math.abs(Number(value.drop_longitude)) > 180) {
        throw failure('INVALID_COORDINATES');
      }
    }
    return value;
  }

  function formatApiCoordinate(value) {
    var parts = String(value).trim().toLowerCase().split('e');
    var fraction = (parts[0].split('.')[1] || '').length;
    var precision = Math.max(0, fraction - Number(parts[1] || 0));
    var missing = Math.max(0, 7 - precision);
    var rounded = Number(value).toFixed(7);
    return missing ? rounded.slice(0, -missing) + '5'.repeat(missing) : rounded;
  }

  function invalidate() {
    generation += 1;
    cache.clear();
    root.clearTimeout(timer);
    timer = null;
    pending.forEach(function (task) { task.reject(failure('STALE')); });
    pending.clear();
    if (active) active.controller.abort();
  }

  function schedule() {
    if (timer || active || !pending.size) return;
    timer = root.setTimeout(flush, 40);
  }

  async function post(body, controller) {
    var response = await root.fetch(ENDPOINT, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {'Content-Type': 'application/json', 'Accept': 'application/json'},
      body: JSON.stringify(body),
      signal: controller.signal
    });
    if (response.status === 429 || response.status === 503) {
      var retryAfter = Number(response.headers.get('Retry-After'));
      var delay = Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter * 1000 : 600;
      if (delay <= 1500 && !controller.signal.aborted) {
        await new Promise(function (resolve) { root.setTimeout(resolve, delay + Math.floor(Math.random() * 100)); });
        if (controller.signal.aborted) throw failure('STALE');
        response = await root.fetch(ENDPOINT, {
          method: 'POST', credentials: 'same-origin',
          headers: {'Content-Type': 'application/json', 'Accept': 'application/json'},
          body: JSON.stringify(body), signal: controller.signal
        });
      }
    }
    if (!response.ok) throw failure('HTTP_' + response.status);
    var payload = await response.json();
    if (!payload || !Array.isArray(payload.results)) throw failure('INVALID_RESPONSE');
    return payload.results;
  }

  async function flush() {
    timer = null;
    if (active || !pending.size) return;
    var tasks = Array.from(pending.values()).slice(0, 25);
    tasks.forEach(function (task) { pending.delete(task.sku); });
    var job = {generation: generation, controller: new AbortController(), tasks: tasks};
    active = job;
    var timeout = root.setTimeout(function () { job.controller.abort(); }, 12000);
    try {
      var body = Object.assign({skus: tasks.map(function (task) { return task.sku; })}, tasks[0].destination);
      var results = await post(body, job.controller);
      if (generation !== job.generation) throw failure('STALE');
      var bySku = new Map();
      results.forEach(function (result) {
        if (!result || typeof result.sku !== 'string' || bySku.has(result.sku)) throw failure('INVALID_RESPONSE');
        bySku.set(result.sku, result);
      });
      tasks.forEach(function (task) {
        var result = bySku.get(task.sku);
        if (!result) {
          cache.delete(task.sku);
          task.reject(failure('MISSING_RESULT'));
          return;
        }
        // Keep only display information. Routing properties never enter this cache.
        var safe = {sku: task.sku, ok: result.ok === true, delivery: result.delivery || {}, error: result.error || null};
        if (safe.ok) cache.set(task.sku, {promise: Promise.resolve(safe), expires: Date.now() + TTL});
        else cache.delete(task.sku);
        task.resolve(safe);
      });
    } catch (error) {
      tasks.forEach(function (task) {
        if (generation === job.generation) cache.delete(task.sku);
        task.reject(error);
      });
    } finally {
      root.clearTimeout(timeout);
      if (active === job) active = null;
      schedule();
    }
  }

  function request(sku, pin, coordinates, enabled) {
    if (!enabled) return Promise.resolve(null);
    var target;
    try { target = destination(pin, coordinates); } catch (error) { return Promise.reject(error); }
    var key = JSON.stringify(target);
    if (key !== destinationKey) {
      invalidate();
      destinationKey = key;
    }
    sku = String(sku || '').trim();
    if (!sku || sku.length > 128) return Promise.reject(failure('INVALID_SKU'));
    var cached = cache.get(sku);
    if (cached && cached.expires > Date.now()) return cached.promise;
    var promise = new Promise(function (resolve, reject) {
      pending.set(sku, {sku: sku, destination: target, resolve: resolve, reject: reject});
    });
    cache.set(sku, {promise: promise, expires: Infinity});
    schedule();
    return promise;
  }

  root.LibasPdpDeliveryEstimates = {request: request, invalidate: invalidate};
  root.dispatchEvent(new CustomEvent('libas:pdp-estimates-ready'));
})(window);
